import PortalDialog, {PortalDialogProperties, PortalDialogState} from "../PortalDialog";
import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import PortalDialogType from "../../PortalDialogType";
import {Button} from "primereact/button";
import Parameter from "../../parameter/Parameter";
import PortalDocument from "../../../document/PortalDocument";
import MimeTypeFilter from "../../../document/mimetype/MimeTypeFilter";
import MimeTypes from "../../../document/mimetype/MimeTypes";
import {WebServiceType, WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import PortalDocumentManager from "../../../document/PortalDocumentManager";
import DialogDefault from "../../parameter/PortalDialogDefault";
import {InputText} from "primereact/inputtext";
import OutlineAddFields from "../../field/document/OutlineAddFields";
import {TabPanel, TabView} from "primereact/tabview";
import {Dropdown} from "primereact/dropdown";
import {ColorPicker} from "primereact/colorpicker";
import {InputSwitch} from "primereact/inputswitch";
import {InputNumber} from "primereact/inputnumber";
import UnitHelper from "../../../../shared/helper/UnitHelper";
import LengthUnit from "../../../../shared/units/LengthUnit";
import {InputTextarea} from "primereact/inputtextarea";
import {Outline} from "../../../document/components/pdf/OutlineList";
import validator from "validator";
import MimeType from "../../../document/mimetype/MimeType";
import {PATH_SEPARATOR} from "../../../../shared/helper/FileHelper";
import DropdownNumber from "../../../../shared/components/DropdownNumber";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import {
	AddToolboxOutline,
	AdminWebserviceStatus,
	BaseToolbox,
	FitBoundingBoxDestination,
	FitBoundingBoxWidthDestination,
	FitBoundingBoxWidthDestinationFitBoundingBoxWidthInterface,
	FitHeightDestination,
	FitHeightDestinationFitHeightInterface,
	FitPageDestination,
	FitRectangleDestination,
	FitRectangleDestinationFitRectangleInterface,
	FitWidthDestination,
	FitWidthDestinationFitWidthInterface,
	Functions,
	GoToDestinationAction,
	Information,
	Item,
	JavaScriptAction,
	JavaScriptActionExecuteJavaScript,
	Metrics,
	NamedAction,
	NamedActionExecuteNamed,
	Security,
	Settings,
	SettingsInterface,
	SignatureEditProfile,
	ToolboxOutlineOutline,
	URIAction,
	URIActionResolveURI,
	ZoomDestination,
	ZoomDestinationZoomPage,
	ZoomDestinationZoomPageInterface
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import {
	PortalGoToDestinationActionGoTo,
	PortalGoToDestinationZoomType
} from "../../../../shared/models/PortalGoToDestinationActionGoTo";
import {
	PortalNamedActionExecuteNamed,
	PortalNamedActionOperation
} from "../../../../shared/models/PortalNamedActionExecuteNamed";
import {PortalActionEvent, PortalActionType} from "../../../../shared/models/PortalActionEvent";
import {PortalItem, PortalItemPosition} from "../../../../shared/models/PortalItem";
import PortalDialogSecurity, {Intention} from "../../PortalDialogSecurity";
import Translate from "../../../../shared/translation/Translate";
import Helper from "../../../../shared/components/Helper";
import Icon from "../../../../shared/components/Icon";
import UserSettings from "../../../session/UserSettings";
import Immutable from "../../../../shared/immutable/Immutable";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";
import {PortalNamedAction} from "../../../../shared/models/PortalNamedAction";
import {pdfjs} from "react-pdf";

interface OutlineAddDialogProperties extends PortalDialogProperties<ToolboxOutlineOutline> {
	userSettings: Immutable<UserSettings>;
}

interface OutlineAddDialogStatus extends PortalDialogState<ToolboxOutlineOutline> {
	outlines: Array<Outline>;
	actionParameter: PortalActionEvent;
	panel: number;
	pages: number;
	unit: LengthUnit;
}

export class OutlineAddDialogDefinition extends PortalDialog<ToolboxOutlineOutline,
	OutlineAddDialogProperties, OutlineAddDialogStatus> {
	constructor(props: any) {
		super(props);

		this.state = {
			...this.state,
			outlines: [],
			actionParameter: PortalActionEvent.fromJson({}),
			panel: 0,
			pages: 1,
			fieldErrors: new Map<OutlineAddFields, string>(),
			unit: LengthUnit.MILLIMETER
		};
	}

	public static isEnabled(portalInfo: Information): boolean {
		return FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.toolbox?.enabled, AdminWebserviceStatus.getEnabledDefault()
		) && FunctionHelper.applyValue(
			portalInfo.functionsStatus?.outline, Functions.getOutlineDefault()
		);
	}

	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		let mimeTypes: Array<MimeType> = [];

		for (let selectedDocument of selectedDocuments) {
			mimeTypes.push(selectedDocument.getMimeType());

			if (selectedDocument.isLocked()) {
				return false;
			}

			if (!PortalDialogSecurity.checkRestrictions(
				PortalDialogType.ADD_OUTLINE, signatureEditProfile, Intention.editingContent, selectedDocument
			)) {
				return false;
			}

			if (!selectedDocument.isEditable()) {
				return false;
			}
		}

		return this.isEnabled(portalInfo) && selectedDocuments.length === 1 &&
			MimeTypeFilter.checkSelection(mimeTypes, [MimeTypes.PDF]);
	}

	private buildOutlineItems(outlines: Array<Outline>, level?: number, parentPath?: string): Array<any> {
		let result: Array<any> = [];

		for (let outline of outlines) {
			let path: string = FunctionHelper.applyValue(parentPath, "") + PATH_SEPARATOR + outline.title;

			result.push({
				label: outline.title,
				value: path,
				level: level
			});

			if (outline.items.length > 0) {
				result = result.concat(this.buildOutlineItems(
					outline.items, FunctionHelper.applyValue(level, 0) + 1, path
				));
			}
		}

		return result;
	}

	private renderGeneralTab(parameter: PortalItem): ReactNode {
		let errors: Map<OutlineAddFields, string> = this.state.fieldErrors;

		if (!FunctionHelper.exists(parameter)) {
			return null;
		}

		return (
			<>
				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(OutlineAddFields.NAME)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-name">
							<Translate {...this.props} data="outline.general.settings.name" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<InputText id="control-name"
									   value={FunctionHelper.applyValue(
										   parameter.itemName, Item.getItemNameDefault()
									   )}
									   onInput={(e) => this.onChange(
										   OutlineAddFields.NAME, e.currentTarget.value
									   )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(OutlineAddFields.POSITION)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-position">
							<Translate {...this.props} data="outline.general.settings.position.label"
									   namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-position"
									  value={FunctionHelper.applyValue(
										  parameter.position, PortalItem.getPositionDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "outline.general.settings.position.end", {ns: "dialog"}
											  ),
											  value: PortalItemPosition.End
										  },
										  {
											  label: Translate.value(
												  "outline.general.settings.position.before", {ns: "dialog"}
											  ),
											  value: PortalItemPosition.Before,
											  disabled: this.state.outlines.length === 0
										  },
										  {
											  label: Translate.value(
												  "outline.general.settings.position.inplace",
												  {ns: "dialog"}
											  ),
											  value: PortalItemPosition.Inplace,
											  disabled: this.state.outlines.length === 0
										  },
										  {
											  label: Translate.value(
												  "outline.general.settings.position.after", {ns: "dialog"}
											  ),
											  value: PortalItemPosition.After,
											  disabled: this.state.outlines.length === 0
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  OutlineAddFields.POSITION, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						parameter.position === PortalItemPosition.End ||
						!this.state.fields?.contains(OutlineAddFields.TARGET)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-target">
							<Translate {...this.props} data="outline.general.settings.target" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-target"
									  value={FunctionHelper.applyValue(parameter.path, Item.getPathDefault())}
									  options={this.buildOutlineItems(this.state.outlines, 0)}
									  itemTemplate={(option: any) =>
										  <>
											  <span dangerouslySetInnerHTML={{
												  __html: "&nbsp;".repeat(
													  FunctionHelper.applyValue(option.level, 0) * 3
												  ) + option.label
											  }}/>
										  </>
									  }
									  onChange={(e) => this.onChange(
										  OutlineAddFields.TARGET, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(OutlineAddFields.COLOR)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-color">
							<Translate {...this.props} data="outline.general.display.color" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<div className="p-inputgroup">
								<InputText className={errors.has(OutlineAddFields.COLOR) ? "p-invalid" : ""}
										   tooltip={errors.has(OutlineAddFields.COLOR) ? Translate.value(
											   errors.get(OutlineAddFields.COLOR)!, {ns: "message"}
										   ) : ""}
										   value={FunctionHelper.applyValue(parameter.color, Item.getColorDefault())}
										   onInput={(e) => this.onChange(
											   OutlineAddFields.COLOR, e.currentTarget.value
										   )}/>
								<ColorPicker inputId="control-color" className="p-inputgroup-addon" format="hex"
											 defaultColor={
												 Item.getColorDefault().replace("#", "")
											 }
											 value={FunctionHelper.applyValue(
												 parameter.color, Item.getColorDefault()
											 ).replace("#", "")}
											 onChange={(e) => this.onChange(
												 OutlineAddFields.COLOR, "#" + e.value
											 )}
											 appendTo={document.getElementById("root")}/>
							</div>
							{this.renderFieldError(errors.has(OutlineAddFields.COLOR) ?
								Translate.value(
									errors.get(OutlineAddFields.COLOR)!, {ns: "message"}
								) : ""
							)}
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(OutlineAddFields.BOLD)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-bold">
							<Translate {...this.props} data="outline.general.display.bold" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-bold"
										 checked={FunctionHelper.applyValue(parameter.bold, Item.getBoldDefault())}
										 onChange={(e) => this.onChange(
											 OutlineAddFields.BOLD, e.value
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(OutlineAddFields.OPEN)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-open">
							<Translate {...this.props} data="outline.general.display.open" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-open"
										 checked={FunctionHelper.applyValue(parameter.isOpen, Item.getIsOpenDefault())}
										 onChange={(e) => this.onChange(
											 OutlineAddFields.OPEN, e.value
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(OutlineAddFields.ITALIC)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-italic">
							<Translate {...this.props} data="outline.general.display.italic" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 switch-container">
							<InputSwitch inputId="control-italic"
										 checked={FunctionHelper.applyValue(parameter.italic, Item.getItalicDefault())}
										 onChange={(e) => this.onChange(
											 OutlineAddFields.ITALIC, e.value
										 )}/>
						</div>
					</div>
				</fieldset>
			</>
		);
	}

	private renderActionTab(parameter: PortalItem): ReactNode {
		let actionParameter: PortalActionEvent = this.state.actionParameter;
		let gotoParameter: PortalGoToDestinationActionGoTo | undefined =
			(actionParameter as GoToDestinationAction)._goTo;
		let namedActionParameter: PortalNamedActionExecuteNamed | undefined =
			(actionParameter as NamedAction).executeNamed;
		let errors: Map<OutlineAddFields, string> = this.state.fieldErrors;

		let gotoPage: number = 1;
		let gotoZoom: number = 100;
		let gotoTop: number = 0;
		let gotoBottom: number = 0;
		let gotoLeft: number = 0;
		let gotoRight: number = 0;

		if (FunctionHelper.exists(gotoParameter?.destination)) {
			switch (gotoParameter!.zoomType) {
				case PortalGoToDestinationZoomType.ZoomPage:
					let zoomParameter: ZoomDestination = gotoParameter!.destination!;
					gotoPage = zoomParameter.zoomPage!.page!;
					gotoZoom = zoomParameter.zoomPage!.zoom!;
					gotoLeft = zoomParameter.zoomPage!.leftOffset!;
					gotoTop = zoomParameter.zoomPage!.topOffset!;
					break;
				case PortalGoToDestinationZoomType.FitBoundingBox:
					let fitBoundingBoxParameter: FitBoundingBoxDestination = gotoParameter!.destination!;
					gotoPage = fitBoundingBoxParameter.fitBoundingBox!.page!;
					break;
				case PortalGoToDestinationZoomType.FitBoundingBoxWidth:
					let fitBoundingBoxWidthParameter: FitBoundingBoxWidthDestination = gotoParameter!.destination!;
					gotoPage = fitBoundingBoxWidthParameter.fitBoundingBoxWidth!.page!;
					gotoTop = fitBoundingBoxWidthParameter.fitBoundingBoxWidth!.topOffset!;
					break;
				case PortalGoToDestinationZoomType.FitRectangle:
					let fitRectangleParameter: FitRectangleDestination = gotoParameter!.destination!;
					gotoPage = fitRectangleParameter.fitRectangle!.page!;
					gotoTop = fitRectangleParameter.fitRectangle!.topOffset!;
					gotoBottom = fitRectangleParameter.fitRectangle!.bottomOffset!;
					gotoLeft = fitRectangleParameter.fitRectangle!.leftOffset!;
					gotoRight = fitRectangleParameter.fitRectangle!.rightOffset!;
					break;
				case PortalGoToDestinationZoomType.FitWidth:
					let fitWidthParameter: FitWidthDestination = gotoParameter!.destination!;
					gotoPage = fitWidthParameter.fitWidth!.page!;
					gotoTop = fitWidthParameter.fitWidth!.topOffset!;
					break;
				case PortalGoToDestinationZoomType.FitHeight:
					let fitHeightParameter: FitHeightDestination = gotoParameter!.destination!;
					gotoPage = fitHeightParameter.fitHeight!.page!;
					gotoLeft = fitHeightParameter.fitHeight!.leftOffset!;
					break;
				case PortalGoToDestinationZoomType.FitPage:
					let fitPageParameter: FitPageDestination = gotoParameter!.destination!;
					gotoPage = fitPageParameter.fitPage!.page!;
					break;
			}
		}

		return (
			<>
				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(OutlineAddFields.ACTION)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-action">
							<Translate {...this.props} data="outline.actions.settings.action.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-action"
									  value={FunctionHelper.applyValue(
										  actionParameter.type, PortalActionEvent.getTypeDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "outline.actions.settings.action.none", {ns: "dialog"}
											  ),
											  value: PortalActionType.None
										  },
										  {
											  label: Translate.value(
												  "outline.actions.settings.action.go_to", {ns: "dialog"}
											  ),
											  value: PortalActionType.GoTo
										  },
										  // {
										  //   label: Translate.value(
										  // 	  "outline.actions.settings.action.sound", {ns: "dialog"}
										  //   ),
										  //   value: PortalActionType.PlaySound
										  // },
										  // {
										  //   label: Translate.value(
										  // 	  "outline.actions.settings.action.application", {ns: "dialog"}
										  //   ),
										  //   value: PortalActionType.LaunchApplication
										  // },
										  // {
										  //   label: Translate.value(
										  // 	  "outline.actions.settings.action.layer", {ns: "dialog"}
										  //   ),
										  //   value: PortalActionType.SetLayerState
										  // },
										  // {
										  //   label: Translate.value(
										  // 	  "outline.actions.settings.action.form", {ns: "dialog"}
										  //   ),
										  //   value: PortalActionType.SubmitForm
										  // },
										  // {
										  //   label: Translate.value(
										  // 	  "outline.actions.settings.action.reset", {ns: "dialog"}
										  //   ),
										  //   value: PortalActionType.ResetForm
										  // },
										  // {
										  //   label: Translate.value(
										  // 	  "outline.actions.settings.action.import", {ns: "dialog"}
										  //   ),
										  //   value: PortalActionType.ImportSchema
										  // },
										  // {
										  //   label: Translate.value(
										  // 	  "outline.actions.settings.action.go_to_thread", {ns: "dialog"}
										  //   ),
										  //   value: PortalActionType.GoToThread
										  // },
										  // {
										  //   label: Translate.value(
										  // 	  "outline.actions.settings.action.go_to_3d_view", {ns: "dialog"}
										  //   ),
										  //   value: PortalActionType.GoTo3DView
										  // },
										  {
											  label: Translate.value(
												  "outline.actions.settings.action.javascript",
												  {ns: "dialog"}
											  ),
											  value: PortalActionType.ExecuteJavaScript
										  },
										  {
											  label: Translate.value(
												  "outline.actions.settings.action.uri", {ns: "dialog"}
											  ),
											  value: PortalActionType.ResolveURI
										  },
										  // {
										  //   label: Translate.value(
										  // 	  "outline.actions.settings.action.transition", {ns: "dialog"}
										  //   ),
										  //   value: PortalActionType.SetTransition
										  // },
										  // {
										  //   label: Translate.value(
										  // 	  "outline.actions.settings.action.hide_annotation", {ns: "dialog"}
										  //   ),
										  //   value: PortalActionType.HideAnnotation
										  // },
										  {
											  label: Translate.value(
												  "outline.actions.settings.action.execute", {ns: "dialog"}
											  ),
											  value: PortalActionType.ExecuteNamed
										  }
									  ]}
									  onChange={(e) => this.onChange(OutlineAddFields.ACTION, e.value)}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						actionParameter.type !== PortalActionType.GoTo ||
						!this.state.fields?.contains(OutlineAddFields.ZOOM)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-zoom">
							<Translate {...this.props} data="outline.actions.settings.zoom.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-zoom"
									  value={FunctionHelper.applyValue(
										  gotoParameter?.zoomType, PortalGoToDestinationActionGoTo.getZoomTypeDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "outline.actions.settings.zoom.bounding_box",
												  {ns: "dialog"}
											  ),
											  value: PortalGoToDestinationZoomType.FitBoundingBox
										  },
										  {
											  label: Translate.value(
												  "outline.actions.settings.zoom.bounding_box_width",
												  {ns: "dialog"}
											  ),
											  value: PortalGoToDestinationZoomType.FitBoundingBoxWidth
										  },
										  {
											  label: Translate.value(
												  "outline.actions.settings.zoom.page", {ns: "dialog"}
											  ),
											  value: PortalGoToDestinationZoomType.FitPage
										  },
										  {
											  label: Translate.value(
												  "outline.actions.settings.zoom.height", {ns: "dialog"}
											  ),
											  value: PortalGoToDestinationZoomType.FitHeight
										  },
										  {
											  label: Translate.value(
												  "outline.actions.settings.zoom.width", {ns: "dialog"}
											  ),
											  value: PortalGoToDestinationZoomType.FitWidth
										  },
										  {
											  label: Translate.value(
												  "outline.actions.settings.zoom.rectangle", {ns: "dialog"}
											  ),
											  value: PortalGoToDestinationZoomType.FitRectangle
										  },
										  {
											  label: Translate.value(
												  "outline.actions.settings.zoom.rectangle_zoom",
												  {ns: "dialog"}
											  ),
											  value: PortalGoToDestinationZoomType.ZoomPage
										  },
										  // {
										  //   label: Translate.value(
										  // 	"outline.actions.settings.zoom.",
										  // 	{ns: "dialog"}
										  // ),
										  //   value: PortalGoToDestinationZoomType.GoToNamed
										  // }
									  ]}
									  onChange={(e) => this.onChange(OutlineAddFields.ZOOM, e.value)}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid" hidden={actionParameter.type !== PortalActionType.ExecuteNamed}>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(OutlineAddFields.EXECUTE_ACTION)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-execute-action">
							<Translate {...this.props} data="outline.actions.execute.action" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-execute-action"
									  value={FunctionHelper.applyValue(
										  namedActionParameter?.operation,
										  PortalNamedActionExecuteNamed.getOperationDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "outline.actions.execute.next", {ns: "dialog"}
											  ),
											  value: PortalNamedActionOperation.NextPage
										  },
										  {
											  label: Translate.value(
												  "outline.actions.execute.previous", {ns: "dialog"}
											  ),
											  value: PortalNamedActionOperation.PrevPage
										  },
										  {
											  label: Translate.value(
												  "outline.actions.execute.first", {ns: "dialog"}
											  ),
											  value: PortalNamedActionOperation.FirstPage
										  },
										  {
											  label: Translate.value(
												  "outline.actions.execute.last", {ns: "dialog"}
											  ),
											  value: PortalNamedActionOperation.LastPage
										  },
										  {
											  label: Translate.value(
												  "outline.actions.execute.custom", {ns: "dialog"}
											  ),
											  value: PortalNamedActionOperation.Custom
										  }
									  ]}
									  onChange={(e) => this.onChange(OutlineAddFields.EXECUTE_ACTION, e.value)}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						namedActionParameter?.operation !== PortalNamedActionOperation.Custom ||
						!this.state.fields?.contains(OutlineAddFields.EXECUTE_CUSTOM)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-execute-custom">
							<Translate {...this.props} data="outline.actions.execute.custom" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<InputText id="control-execute-custom"
									   value={FunctionHelper.applyValue(
										   namedActionParameter?.namedOperation,
										   NamedActionExecuteNamed.getNamedOperationDefault()
									   )}
									   onInput={(e) => this.onChange(
										   OutlineAddFields.EXECUTE_CUSTOM, e.currentTarget.value
									   )}/>
						</div>
					</div>
					<div className="p-col-12 p-grid">
						<div className="p-col-12 p-lg-6 p-grid">
							<label className="p-col-12 p-sm-5" htmlFor="control-javascript"/>
							<div className="p-col-12 p-sm-7 p-grid">
								<Button label={Translate.value("buttons.add")} icon="icon icon-add"
										onClick={() => {
											parameter.actions?.push(actionParameter.clone());

											this.setState({
												actionParameter: PortalActionEvent.fromJson({})
											});
										}} className="dialog-button" disabled={errors.size > 0}/>
							</div>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid" hidden={actionParameter.type !== PortalActionType.ResolveURI}>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(OutlineAddFields.ACTION_URI)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-uri">
							<Translate {...this.props} data="outline.actions.uri.uri" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<InputText id="control-uri"
									   className={errors.has(OutlineAddFields.ACTION_URI) ? "p-invalid" : ""}
									   tooltip={errors.has(OutlineAddFields.ACTION_URI) ? Translate.value(
										   errors.get(OutlineAddFields.ACTION_URI)!, {ns: "message"}
									   ) : ""}
									   value={FunctionHelper.applyValue(
										   (actionParameter as URIAction).resolveURI?.uri,
										   URIActionResolveURI.getUriDefault()
									   )}
									   onInput={(e) => this.onChange(
										   OutlineAddFields.ACTION_URI, e.currentTarget.value
									   )}/>
							{this.renderFieldError(errors.has(OutlineAddFields.ACTION_URI) ?
								Translate.value(
									errors.get(OutlineAddFields.ACTION_URI)!, {ns: "message"}
								) : ""
							)}
						</div>
					</div>
					<div className="p-col-12 p-grid">
						<div className="p-col-12 p-lg-6 p-grid">
							<label className="p-col-12 p-sm-5" htmlFor="control-javascript"/>
							<div className="p-col-12 p-sm-7 p-grid">
								<Button label={Translate.value("buttons.add")} icon="icon icon-add"
										onClick={() => {
											parameter.actions?.push(actionParameter.clone());

											this.setState({
												actionParameter: PortalActionEvent.fromJson({})
											});
										}} className="dialog-button" disabled={errors.size > 0}/>
							</div>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid"
						  hidden={actionParameter.type !== PortalActionType.ExecuteJavaScript}>
					<div className="p-col-12 p-lg-6 p-grid"
						 hidden={!this.state.fields?.contains(OutlineAddFields.ACTION_JAVASCRIPT)}>
						<label className="p-col-12 p-sm-5" htmlFor="control-javascript">
							<Translate {...this.props} data="outline.actions.javascript.action" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<InputTextarea rows={5} id="control-javascript"
										   value={FunctionHelper.applyValue(
											   (actionParameter as JavaScriptAction).executeJavaScript?.jsAction,
											   JavaScriptActionExecuteJavaScript.getJsActionDefault()
										   )}
										   onInput={(e) => this.onChange(
											   OutlineAddFields.ACTION_JAVASCRIPT, e.currentTarget.value
										   )}/>
						</div>
					</div>
					<div className="p-col-12 p-grid">
						<div className="p-col-12 p-lg-6 p-grid">
							<label className="p-col-12 p-sm-5" htmlFor="control-javascript"/>
							<div className="p-col-12 p-sm-7 p-grid">
								<Button label={Translate.value("buttons.add")} icon="icon icon-add"
										onClick={() => {
											parameter.actions?.push(
												(actionParameter as PortalActionEvent).clone()
											);

											this.setState({
												actionParameter: PortalActionEvent.fromJson({})
											});
										}} className="dialog-button" disabled={errors.size > 0}/>
							</div>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid" hidden={actionParameter.type !== PortalActionType.GoTo}>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						actionParameter.type !== PortalActionType.GoTo ||
						!this.state.fields?.contains(OutlineAddFields.GO_TO_PAGE)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-goto-page">
							<Translate {...this.props} data="outline.actions.goto.page" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<InputNumber inputId="control-goto-page" showButtons={true} useGrouping={false}
										 value={gotoPage} max={this.state.pages} min={1} step={1}
										 onChange={(e) => this.onChange(
											 OutlineAddFields.GO_TO_PAGE, e.value
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						actionParameter.type !== PortalActionType.GoTo ||
						gotoParameter?.zoomType !== PortalGoToDestinationZoomType.ZoomPage ||
						!this.state.fields?.contains(OutlineAddFields.GO_TO_ZOOM)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-goto-zoom">
							<Translate {...this.props} data="outline.actions.goto.zoom" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<DropdownNumber inputId="control-goto-zoom" value={gotoZoom} suffix=" %" showButtons={true}
											onChange={(e) => this.onChange(
												OutlineAddFields.GO_TO_ZOOM, e.value
											)}
											appendTo={document.getElementById("root")} min={1} max={800}
											options={[
												{label: "10", value: 10},
												{label: "25", value: 25},
												{label: "50", value: 50},
												{label: "75", value: 75},
												{label: "100", value: 100},
												{label: "125", value: 125},
												{label: "150", value: 150},
												{label: "200", value: 200},
												{label: "400", value: 400},
												{label: "800", value: 800}
											]}/>
						</div>
					</div>

					<div className="p-col-12 p-lg-6 p-grid" hidden={
						actionParameter.type !== PortalActionType.GoTo ||
						(
							gotoParameter?.zoomType !== PortalGoToDestinationZoomType.ZoomPage &&
							gotoParameter?.zoomType !== PortalGoToDestinationZoomType.FitBoundingBoxWidth &&
							gotoParameter?.zoomType !== PortalGoToDestinationZoomType.FitRectangle &&
							gotoParameter?.zoomType !== PortalGoToDestinationZoomType.FitWidth
						) ||
						!this.state.fields?.contains(OutlineAddFields.GO_TO_TOP_OFFSET)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-goto-top">
							<Translate {...this.props} data="outline.actions.goto.offset.top" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<InputNumber inputId="control-goto-top" showButtons={true} maxFractionDigits={2}
										 minFractionDigits={2} suffix={" " + this.state.unit} useGrouping={false}
										 value={UnitHelper.unitToUnit(
											 gotoTop, LengthUnit.MILLIMETER, this.state.unit,
											 this.props.portalResolution
										 )} min={0} step={1}
										 onChange={(e) => this.onChange(
											 OutlineAddFields.GO_TO_TOP_OFFSET, e.value
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						actionParameter.type !== PortalActionType.GoTo ||
						gotoParameter?.zoomType !== PortalGoToDestinationZoomType.FitRectangle ||
						!this.state.fields?.contains(OutlineAddFields.GO_TO_BOTTOM_OFFSET)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-goto-bottom">
							<Translate {...this.props} data="outline.actions.goto.offset.bottom" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<InputNumber inputId="control-goto-bottom" showButtons={true} maxFractionDigits={2}
										 minFractionDigits={2} suffix={" " + this.state.unit} useGrouping={false}
										 value={UnitHelper.unitToUnit(
											 gotoBottom, LengthUnit.MILLIMETER, this.state.unit,
											 this.props.portalResolution
										 )} min={0} step={1}
										 onChange={(e) => this.onChange(
											 OutlineAddFields.GO_TO_BOTTOM_OFFSET, e.value
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						actionParameter.type !== PortalActionType.GoTo ||
						(
							gotoParameter?.zoomType !== PortalGoToDestinationZoomType.ZoomPage &&
							gotoParameter?.zoomType !== PortalGoToDestinationZoomType.FitRectangle &&
							gotoParameter?.zoomType !== PortalGoToDestinationZoomType.FitHeight
						) ||
						!this.state.fields?.contains(OutlineAddFields.GO_TO_LEFT_OFFSET)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-goto-left">
							<Translate {...this.props} data="outline.actions.goto.offset.left" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<InputNumber inputId="control-goto-left" showButtons={true} maxFractionDigits={2}
										 minFractionDigits={2} suffix={" " + this.state.unit} useGrouping={false}
										 value={UnitHelper.unitToUnit(
											 gotoLeft, LengthUnit.MILLIMETER, this.state.unit,
											 this.props.portalResolution
										 )} min={0} step={1}
										 onChange={(e) => this.onChange(
											 OutlineAddFields.GO_TO_LEFT_OFFSET, e.value
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						actionParameter.type !== PortalActionType.GoTo ||
						gotoParameter?.zoomType !== PortalGoToDestinationZoomType.FitRectangle ||
						!this.state.fields?.contains(OutlineAddFields.GO_TO_RIGHT_OFFSET)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-goto-right">
							<Translate {...this.props} data="outline.actions.goto.offset.right" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7">
							<InputNumber inputId="control-goto-right" showButtons={true} maxFractionDigits={2}
										 minFractionDigits={2} suffix={" " + this.state.unit} useGrouping={false}
										 value={UnitHelper.unitToUnit(
											 gotoRight, LengthUnit.MILLIMETER, this.state.unit,
											 this.props.portalResolution
										 )} min={0} step={1}
										 onChange={(e) => this.onChange(
											 OutlineAddFields.GO_TO_RIGHT_OFFSET, e.value
										 )}/>
						</div>
					</div>
					<div className="p-col-12 p-grid">
						<div className="p-col-12 p-lg-6 p-grid">
							<label className="p-col-12 p-sm-5" htmlFor="control-javascript"/>
							<div className="p-col-12 p-sm-7 p-grid">
								<Button label={Translate.value("buttons.add")} icon="icon icon-add"
										onClick={() => {
											parameter.actions?.push(
												(actionParameter as PortalActionEvent).clone()
											);

											this.setState({
												actionParameter: PortalActionEvent.fromJson({})
											});
										}} className="dialog-button" disabled={errors.size > 0}/>
							</div>
						</div>
					</div>
				</fieldset>

				{this.renderActionList(parameter)}
			</>
		);
	}

	private renderActionList(parameter: PortalItem): ReactNode {
		if (!FunctionHelper.exists(parameter)) {
			return null;
		}

		let actionItems: Array<ReactNode> = [];

		for (let [actionIndex, action] of (FunctionHelper.applyValue(
			parameter.actions, Item.getActionsDefault()
		) as Array<PortalActionEvent>).entries()) {
			let actionName: string = "";

			switch (action.type) {
				case PortalActionType.GoTo:
					let gotoParameter: PortalGoToDestinationActionGoTo = (
						action as GoToDestinationAction
					)._goTo as PortalGoToDestinationActionGoTo;

					switch (gotoParameter.zoomType) {
						case PortalGoToDestinationZoomType.FitBoundingBox:
							actionName = Translate.value(
								"outline.actions.list.bounding_box", {ns: "dialog"}
							);
							break;
						case PortalGoToDestinationZoomType.FitBoundingBoxWidth:
							actionName = Translate.value(
								"outline.actions.list.bounding_box_width", {ns: "dialog"}
							);
							break;
						case PortalGoToDestinationZoomType.FitHeight:
							actionName = Translate.value(
								"outline.actions.list.height", {ns: "dialog"}
							);
							break;
						case PortalGoToDestinationZoomType.FitPage:
							actionName = Translate.value(
								"outline.actions.list.page", {ns: "dialog"}
							);
							break;
						case PortalGoToDestinationZoomType.FitRectangle:
							actionName = Translate.value(
								"outline.actions.list.rectangle", {ns: "dialog"}
							);
							break;
						case PortalGoToDestinationZoomType.FitWidth:
							actionName = Translate.value(
								"outline.actions.list.width", {ns: "dialog"}
							);
							break;
						case PortalGoToDestinationZoomType.ZoomPage:
							actionName = Translate.value(
								"outline.actions.list.rectangle_zoom", {ns: "dialog"}
							);
							break;
					}
					break;
				case PortalActionType.ExecuteJavaScript:
					actionName = Translate.value(
						"outline.actions.list.javascript", {ns: "dialog"}
					);
					break;
				case PortalActionType.ResolveURI:
					actionName = Translate.value(
						"outline.actions.list.uri",
						{ns: "dialog", url: (action as URIAction).resolveURI?.uri}
					);
					break;
				case PortalActionType.ExecuteNamed:
					actionName = Translate.value(
						"outline.actions.list.execute",
						{ns: "dialog", action: (action as NamedAction).executeNamed?.namedOperation}
					);
					break;
			}

			actionItems.push(
				<div className="action-item" key={actionIndex}>
					<div className="action-name">{actionName}</div>
					<div className="action-functions">
						<Button className="action-first" icon="icon icon-up" onClick={() => {
							let tempItem: PortalActionEvent =
								parameter.actions![actionIndex - 1] as PortalActionEvent;
							parameter.actions![actionIndex - 1] = parameter.actions![actionIndex];
							parameter.actions![actionIndex] = tempItem;

							this.setState({
								parameter: this.state.parameter
							});
						}}/>
						<Button className="action-last" icon="icon icon-down" onClick={() => {
							let tempItem: PortalActionEvent =
								parameter.actions![actionIndex + 1] as PortalActionEvent;
							parameter.actions![actionIndex + 1] = parameter.actions![actionIndex];
							parameter.actions![actionIndex] = tempItem;

							this.setState({
								parameter: this.state.parameter
							});
						}}/>
						<Button icon="icon icon-delete small" onClick={() => {
							parameter.actions!.splice(actionIndex, 1);

							this.setState({
								parameter: this.state.parameter
							});
						}}/>
					</div>
				</div>
			);
		}

		return (
			<>
				<fieldset className="p-col-12 p-grid" hidden={
					FunctionHelper.applyValue(
						parameter.actions, Item.getActionsDefault()
					).length === 0 || !this.state.fields?.contains(OutlineAddFields.LIST)
				}>
					<div className="p-col-12 p-grid">
						<div className="action-list">
							<div className="action-header">
								<Translate {...this.props} data="outline.actions.list.action" namespace="dialog"/>
							</div>
							<div className="action-items">
								{actionItems}
							</div>
						</div>
					</div>
				</fieldset>
			</>
		);
	}

	private renderContents(): ReactNode {
		let parameter: ToolboxOutlineOutline | undefined = this.state.parameter?.data;

		if (!FunctionHelper.exists(parameter?.add)) {
			return null;
		}

		let addParameter: AddToolboxOutline = parameter!.add!;

		return (
			<>
				<TabView activeIndex={this.state.panel}
						 onTabChange={(e) => this.setState({panel: e.index})}>
					<TabPanel header={Translate.value("outline.general.label", {ns: "dialog"})}>
						{this.renderGeneralTab(addParameter.item![0] as PortalItem)}
					</TabPanel>
					<TabPanel header={Translate.value("outline.actions.label", {ns: "dialog"})}>
						{this.renderActionTab(addParameter.item![0] as PortalItem)}
					</TabPanel>
				</TabView>
			</>
		);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["add-outline-dialog", "large"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()}
					appendTo={document.getElementById("root")} resizable={false}>
				{this.renderError()}
				<Helper {...this.props}>
					<Translate {...this.props} data={"dialog.outline.general"} namespace={"help"} markdown={true}/>
				</Helper>
				{this.renderContents()}
			</Dialog>
		);
	}

	protected onChange(field: OutlineAddFields, value: any): void {
		let parameter: ToolboxOutlineOutline = this.state.parameter!.data;
		let addParameter: PortalItem = parameter.add!.item![0] as PortalItem;
		let actionParameter: PortalActionEvent = this.state.actionParameter;
		let gotoParameter: PortalGoToDestinationActionGoTo | undefined =
			(actionParameter as GoToDestinationAction)._goTo as PortalGoToDestinationActionGoTo;
		let namedActionParameter: PortalNamedActionExecuteNamed | undefined =
			(actionParameter as NamedAction).executeNamed as PortalNamedActionExecuteNamed;
		let errors: Map<OutlineAddFields, string> = this.state.fieldErrors;

		switch (field) {
			case OutlineAddFields.NAME:
				addParameter.itemName = value;
				break;
			case OutlineAddFields.POSITION:
				addParameter.position = value;

				if (value === PortalItemPosition.End) {
					addParameter.path = PATH_SEPARATOR;
				}
				break;
			case OutlineAddFields.TARGET:
				addParameter.path = value;
				break;
			case OutlineAddFields.COLOR:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (!validator.isHexColor(value)) {
					errors.set(field, "errors.fields.color");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				addParameter.color = value;
				break;
			case OutlineAddFields.BOLD:
				addParameter.bold = value;
				break;
			case OutlineAddFields.OPEN:
				addParameter.isOpen = value;
				break;
			case OutlineAddFields.ITALIC:
				addParameter.italic = value;
				break;
			case OutlineAddFields.ACTION:
				if (errors.has(OutlineAddFields.ACTION_URI)) {
					errors.delete(OutlineAddFields.ACTION_URI);
				}

				switch (value) {
					case PortalActionType.GoTo:
						actionParameter = PortalActionEvent.fromJson({
							type: value,
							goTo: {
								zoomType: PortalGoToDestinationZoomType.FitBoundingBox,
								destination: {
									fitBoundingBox: {}
								}
							}
						});
						break;
					case PortalActionType.ExecuteJavaScript:
						actionParameter = PortalActionEvent.fromJson({
							type: value,
							executeJavaScript: {}
						});
						break;
					case PortalActionType.ResolveURI:
						actionParameter = PortalActionEvent.fromJson({
							type: value,
							resolveURI: {}
						});
						break;
					case PortalActionType.ExecuteNamed:
						actionParameter = PortalActionEvent.fromJson({
							type: value,
							executeNamed: {
								operation: PortalNamedActionOperation.NextPage,
								namedOperation: PortalNamedActionOperation.NextPage
							}
						} as PortalNamedAction);
						break;
					default:
						actionParameter = PortalActionEvent.fromJson({
							type: value
						} as PortalActionEvent);
						break;
				}
				break;
			case OutlineAddFields.ZOOM:
				switch (value) {
					case PortalGoToDestinationZoomType.ZoomPage:
						gotoParameter = PortalGoToDestinationActionGoTo.fromJson({
							zoomType: value,
							destination: {
								zoomPage: {
									metrics: Metrics.Mm
								} as ZoomDestinationZoomPageInterface
							}
						});
						break;
					case PortalGoToDestinationZoomType.FitBoundingBox:
						gotoParameter = PortalGoToDestinationActionGoTo.fromJson({
							zoomType: value,
							destination: {
								fitBoundingBox: {}
							}
						});
						break;
					case PortalGoToDestinationZoomType.FitBoundingBoxWidth:
						gotoParameter = PortalGoToDestinationActionGoTo.fromJson({
							zoomType: value,
							destination: {
								fitBoundingBoxWidth: {
									metrics: Metrics.Mm
								} as FitBoundingBoxWidthDestinationFitBoundingBoxWidthInterface
							}
						});
						break;
					case PortalGoToDestinationZoomType.FitPage:
						gotoParameter = PortalGoToDestinationActionGoTo.fromJson({
							zoomType: value,
							destination: {
								fitPage: {}
							}
						});
						break;
					case PortalGoToDestinationZoomType.FitHeight:
						gotoParameter = PortalGoToDestinationActionGoTo.fromJson({
							zoomType: value,
							destination: {
								fitHeight: {
									metrics: Metrics.Mm
								} as FitHeightDestinationFitHeightInterface
							}
						});
						break;
					case PortalGoToDestinationZoomType.FitWidth:
						gotoParameter = PortalGoToDestinationActionGoTo.fromJson({
							zoomType: value,
							destination: {
								fitWidth: {
									metrics: Metrics.Mm
								} as FitWidthDestinationFitWidthInterface
							}
						});
						break;
					case PortalGoToDestinationZoomType.FitRectangle:
						gotoParameter = PortalGoToDestinationActionGoTo.fromJson({
							zoomType: value,
							destination: {
								fitRectangle: {
									metrics: Metrics.Mm
								} as FitRectangleDestinationFitRectangleInterface
							}
						});
						break;
					case PortalGoToDestinationZoomType.GoToNamed:
						gotoParameter = PortalGoToDestinationActionGoTo.fromJson({
							zoomType: value,
							destination: {
								goToNamed: {}
							}
						});
						break;
				}

				(actionParameter as GoToDestinationAction)._goTo = gotoParameter;
				break;
			case OutlineAddFields.GO_TO_PAGE:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = 1;
				}

				switch (gotoParameter.zoomType) {
					case PortalGoToDestinationZoomType.ZoomPage:
						(gotoParameter.destination as ZoomDestination).zoomPage!.page = value
						break;
					case PortalGoToDestinationZoomType.FitBoundingBox:
						(gotoParameter.destination as FitBoundingBoxDestination).fitBoundingBox!.page = value
						break;
					case PortalGoToDestinationZoomType.FitBoundingBoxWidth:
						(gotoParameter.destination as FitBoundingBoxWidthDestination).fitBoundingBoxWidth!.page = value
						break;
					case PortalGoToDestinationZoomType.FitPage:
						(gotoParameter.destination as FitPageDestination).fitPage!.page = value
						break;
					case PortalGoToDestinationZoomType.FitHeight:
						(gotoParameter.destination as FitHeightDestination).fitHeight!.page = value
						break;
					case PortalGoToDestinationZoomType.FitWidth:
						(gotoParameter.destination as FitWidthDestination).fitWidth!.page = value
						break;
					case PortalGoToDestinationZoomType.FitRectangle:
						(gotoParameter.destination as FitRectangleDestination).fitRectangle!.page = value
						break;
				}
				break;
			case OutlineAddFields.GO_TO_ZOOM:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = ZoomDestinationZoomPage.getZoomDefault();
				}

				let zoomPageParameter: ZoomDestination = gotoParameter.destination as ZoomDestination;
				zoomPageParameter.zoomPage!.zoom = value;
				break;
			case OutlineAddFields.GO_TO_TOP_OFFSET:
				switch (gotoParameter.zoomType) {
					case PortalGoToDestinationZoomType.ZoomPage:
						(gotoParameter.destination as ZoomDestination).zoomPage!.topOffset =
							UnitHelper.unitToUnit(
								value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
							);
						break;
					case PortalGoToDestinationZoomType.FitBoundingBoxWidth:
						(gotoParameter.destination as FitBoundingBoxWidthDestination).fitBoundingBoxWidth!.topOffset =
							UnitHelper.unitToUnit(
								value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
							);
						break;
					case PortalGoToDestinationZoomType.FitRectangle:
						(gotoParameter.destination as FitRectangleDestination).fitRectangle!.topOffset =
							UnitHelper.unitToUnit(
								value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
							);
						break;
					case PortalGoToDestinationZoomType.FitWidth:
						(gotoParameter.destination as FitWidthDestination).fitWidth!.topOffset =
							UnitHelper.unitToUnit(
								value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
							);
						break;
				}
				break;
			case OutlineAddFields.GO_TO_BOTTOM_OFFSET:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = 0;
				}

				(gotoParameter.destination as FitRectangleDestination).fitRectangle!.bottomOffset =
					UnitHelper.unitToUnit(value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution);
				break;
			case OutlineAddFields.GO_TO_LEFT_OFFSET:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = 0;
				}

				switch (gotoParameter.zoomType) {
					case PortalGoToDestinationZoomType.ZoomPage:
						(gotoParameter.destination as ZoomDestination).zoomPage!.leftOffset =
							UnitHelper.unitToUnit(
								value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
							);
						break;
					case PortalGoToDestinationZoomType.FitRectangle:
						(gotoParameter.destination as FitRectangleDestination).fitRectangle!.leftOffset =
							UnitHelper.unitToUnit(
								value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
							);
						break;
					case PortalGoToDestinationZoomType.FitHeight:
						(gotoParameter.destination as FitHeightDestination).fitHeight!.leftOffset =
							UnitHelper.unitToUnit(
								value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution
							);
						break;
				}
				break;
			case OutlineAddFields.GO_TO_RIGHT_OFFSET:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = 0;
				}

				(gotoParameter.destination as FitRectangleDestination).fitRectangle!.rightOffset =
					UnitHelper.unitToUnit(value, this.state.unit, LengthUnit.MILLIMETER, this.props.portalResolution);
				break;
			case OutlineAddFields.ACTION_JAVASCRIPT:
				(actionParameter as JavaScriptAction).executeJavaScript!.jsAction = value;
				break;
			case OutlineAddFields.ACTION_URI:
				if (!FunctionHelper.isEmpty(value) && FunctionHelper.exists(value) && !validator.isURL(value)) {
					errors.set(field, "errors.fields.url.syntax");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				(actionParameter as URIAction).resolveURI!.uri = value;
				break;
			case OutlineAddFields.EXECUTE_ACTION:
				namedActionParameter.operation = value;
				namedActionParameter.namedOperation = "";

				if (namedActionParameter.operation !== PortalNamedActionOperation.Custom) {
					namedActionParameter.namedOperation = value;
				}
				break;
			case OutlineAddFields.EXECUTE_CUSTOM:
				namedActionParameter.namedOperation = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter,
			actionParameter: actionParameter
		});
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-bookmark"}/>
					<Translate {...this.props} data="outline.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected isEditPasswordNeeded(portalDocument: PortalDocument): boolean {
		return super.isEditPasswordNeeded(portalDocument) && !FunctionHelper.applyValue(
			portalDocument.metadata?.security?.canModify, Security.getCanModifyDefault()
		);
	}

	/**
	 * Handle showing/initializing the dialog
	 */
	protected async onShow(): Promise<void> {
		await super.onShow();

		let parameter: ToolboxOutlineOutline | undefined = this.state.parameter?.data;
		let portalDocument: PortalDocument = this.props.context.documentManager.getSelection()[0];

		if (!FunctionHelper.exists(parameter) || !FunctionHelper.exists(portalDocument)) {
			return;
		}

		let userSettings: UserSettings = UserSettings.fromJson(this.props.userSettings.get());

		try {
			let pdfDocument: pdfjs.PDFDocumentProxy = await portalDocument.loadViewerData();
			this.setState({
				panel: 0,
				parameter: this.state.parameter,
				defaultParameter: this.state.parameter?.clone(),
				pages: await portalDocument.getNumberOfPages(),
				outlines: FunctionHelper.applyValue(await pdfDocument.getOutline(), []),
				unit: userSettings.unit
			});
		} catch (exception: any) {
			if (await this.onError(exception)) {
				await this.onShow();
			}
		}
	};

	protected async submit(): Promise<void> {
		await super.submit();

		let toolboxOperation: BaseToolbox = BaseToolbox.fromJson({
			outline: this.state.parameter!.data.toJson()
		});

		let operationData: Array<BaseToolbox> = [toolboxOperation];
		let webServiceType: WebServiceType = WebServiceTypes.TOOLBOX;
		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();

		for (let portalDocument of selection) {
			try {
				await this.props.context.session.executeRequest({
					webServiceType: webServiceType,
					document: portalDocument,
					operation: operationData,
					settings: Settings.fromJson({
						pdfa: {
							pdfaEditProfile: this.props.pdfaEditProfile
						},
						signature: {
							signatureEditProfile: this.props.signatureEditProfile
						},
						compress: {
							compressProfile: this.props.compressProfile,
							compressObjects: {
								objectStreamSize: this.props.objectStreamSize
							}
						}
					} as SettingsInterface)
				});
			} catch (exception: any) {
				if (await this.onError(exception)) {
					await this.submit();
				}
				return;
			}

			let message: Message = new Message("success.toolbox.outline.add", MessageType.SUCCESS);
			message.context = {filename: '"' + portalDocument.getFileName() + '" '};
			this.props.context.getPage().addMessage(message);
			this.hide();
		}
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.ADD_OUTLINE;
	}

	protected getDefaultParameter(): Parameter<ToolboxOutlineOutline> {
		return DialogDefault.getParameter(
			this.getDialogType(), this.props.context.documentManager.getSelection()
		);
	}

	protected async resetParameter(): Promise<void> {
		await super.resetParameter();

		this.setState({
			panel: 0
		});
	}
}

const OutlineAddDialog = React.memo(OutlineAddDialogDefinition,
	(prevProps: Readonly<OutlineAddDialogProperties>, nextProps: Readonly<OutlineAddDialogProperties>) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default OutlineAddDialog;